// easyui-treegrid.component.ts
// (c) 2017 EvoluS Srl
// (a) VG

import { Component, ElementRef, Input, Output, EventEmitter, ViewChild, AfterViewInit } from '@angular/core';

declare let $: any;

@Component({
  selector: 'easyui-treegrid',
  template: `
    <div style="height: 100%">
      <div #host>
        <ng-content></ng-content>
      </div>
    </div>
  `
})
export class EasyuiTreegridComponent implements AfterViewInit {
  protected host: any;
  protected focuspanel: any;
  // riferimenti alla view
  @ViewChild('host') hostElement: ElementRef;
  // bind in ingresso
  @Input('options') options: any = {};
  // bind in uscita
  @Output() onOverTop = new EventEmitter<void>();
  @Output() onBelowBottom = new EventEmitter<void>();
  @Output() onInvio = new EventEmitter<EasyuiTreegridComponent>();
  @Output() onSpace = new EventEmitter<EasyuiTreegridComponent>();
  @Output() onKeydown = new EventEmitter<any>();
  @Output() onKeypress = new EventEmitter<any>();
  @Output() onBlur = new EventEmitter<EasyuiTreegridComponent>();

  ngAfterViewInit() {
    this.configura(this.options);
  }

  // metodi pubblici

  public loadDati(dati) {
    this.host.treegrid('loadData', dati);
  }

  public setFocus() {
    this.host.treegrid('getPanel').panel('panel').focus();
  }

  public selectId(id: number) {
    this.host.treegrid('select', id);
  }

  public selectAll() {
    this.host.treegrid('selectAll');
  }

  public unselectId(id: number) {
    this.host.treegrid('unselect', id);
  }

  public unselectAll() {
    this.host.treegrid('unselectAll');
  }

  public getSelectedRow() {
    return this.host.treegrid('getSelected');
  }

  public getSelectedId() {
    const opts = this.host.treegrid('options');
    const row = this.getSelectedRow();
    const tr = opts.finder.getTr(this.host[0], row[opts.idField]);
    return tr ? tr.attr('node-id') : 0;
  }

  public isSelectedOpen(): boolean {
    const selected = this.getSelectedRow();
    const isOpen: boolean = (selected && selected.state && (selected.state === 'open')) ? true : false;
    return isOpen;
  }

  public getRowsNum(): number {
    const rows = this.host.treegrid('getData');
    return rows ? rows.length : 0;
  }

  public findRow(id: number) {
    return this.host.treegrid('find', id);
  }

  public expand(id: number) {
    this.host.treegrid('expand', id);
  }

  public collapse(id: number) {
    this.host.treegrid('collapse', id);
  }

  public toggle(id: number) {
    this.host.treegrid('toggle', id);
  }

  public moveRow(to: string) {
    const opts = this.host.treegrid('options');
    const idField = opts.idField;
    const selected = this.getSelectedRow();
    const tr = this.host.treegrid('options').finder.getTr(this.host[0], selected[opts.idField]);
    const isOpen: boolean = (selected && selected.state && (selected.state === 'open')) ? true : false;
    switch (to) {
      case 'up':
        if (!this.prevRow(tr)) {
          this.stepUpPrev(tr);
        }
        break;
      case 'down':
        if (isOpen && selected.children) {
          this.stepDown(tr);
        } else {
          if (!this.nextRow(tr)) {
            this.stepUpNext(tr);
          }
        }
        break;
    }
  }

  // prossima riga, stesso livello
  private nextRow(tablerow): boolean {
    const nextelement = tablerow.nextAll('.datagrid-row');
    if (nextelement.length) {
      const id = nextelement.first().attr('node-id');
      this.host.treegrid('select', id);
    }
    return nextelement.length > 0;
  }

  public styleElement( tag: string, css: any) {
    const element = this.host.find(tag);
    element.css(css);
  }

  // riga precedente, stesso livello
  private prevRow(tablerow): boolean  {
    const prevelement = tablerow.prevAll('.datagrid-row');
    const rowid = this.getSelectedId();
    if (prevelement.length) {
      const id = prevelement.first().attr('node-id');
      const data = this.host.treegrid('find', id);
      const isOpen: boolean = data && data.state && (data.state === 'open');
      if (isOpen) {
        const children = data.children;
        const opts = this.host.treegrid('options');
        if (children && children.length && (children[0][opts.idField] != rowid)) {
          this.host.treegrid('select', children[children.length - 1][opts.idField]);
        } else {
          this.host.treegrid('select', id);
        }
      } else {
        this.host.treegrid('select', id);
      }
    }
    return prevelement.length > 0;
  }

  // prima riga, livello successivo
  private stepDown(tablerow): boolean {
    const nexttree = tablerow.nextAll('.treegrid-tr-tree');
    if (nexttree.length) {
      const nextelement = nexttree.find('.datagrid-row');
      const id = nextelement.first().attr('node-id');
      this.host.treegrid('select', id);
    }
    return nexttree.length > 0;
  }

  // riga precedente, livello precedente
  private stepUpPrev(tablerow) {
    const parentelement = tablerow.parents('.treegrid-tr-tree').first();
    if (!this.prevRow(parentelement)) {  this.onOverTop.emit(); }
  }

  // riga successiva, livello precedente
  private stepUpNext(tablerow) {
    const parentelement = tablerow.parents('.treegrid-tr-tree').first();
    if (!this.nextRow(parentelement)) {  this.onBelowBottom.emit(); }
  }

  public getPanel(): any {
    return this.host.treegrid('getPanel').panel('panel');
  }

  // metodi privati
  protected configura(options: any) {
    const localoptions = {
      fit: true,
      fitColumns: true,
      autoRowHeight: false,
      checkOnSelect: false,
      selectOnCheck: false,
      singleSelect: true,
      remoteSort: false,
      formatter: (node) => {
        node.iconCls = 'icon-search';
      },
      onDblClickRow: () => {
        const selected = this.getSelectedRow();
        if (selected.children) {
          const id = this.getSelectedId();
          this.toggle(id);
        }
      }
    };
    // derivare la funzione jquery dell'elemento html
    this.host = $(this.hostElement.nativeElement);
    // fare un merge dei due oggetti opzioni
    Object.assign(localoptions, options); // copia da destra a sinistra, sovrascrivere campi già presenti
    // creare la datagrid easyui
    this.host.treegrid(localoptions);
    // individuare il panel di focus della grid (c.d. outer panel, in pratica la div html)
    const focuspanel = this.getPanel();
    // stabilire che è sede di tabindex
    focuspanel.attr('tabindex', 1);
    // configurare gli eventi di tastiera per muovere la barra
    focuspanel.on('keydown', (event) => {
      this.onKeydown.emit(event);
      switch (event.keyCode)  {
        case 38:
          this.moveRow('up');
          break;
        case 40:
          this.moveRow('down');
          break;
        case 32:
          const id = this.getSelectedId();
          this.toggle(id);
          this.onSpace.emit(this);
          break;
        case 13:
          this.onInvio.emit(this);
          break;
        }
    });
    focuspanel.on('keypress', (event) => {
      this.onKeypress.emit(event);
    });
    focuspanel.on('blur', () => {
      this.onBlur.emit(this);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/easyui-ts/easyui-treegrid.component.ts